import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

interface GoogleButtonProps {
  text?: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
}

// Always use relative URL to go through React proxy server
// This ensures OAuth works through cloud.doploy.io instead of direct backend connection
const defaultHref = '/oauth/google';

export function GoogleButton({ 
  text = 'Sign in with Google', 
  href = defaultHref,
  onClick,
  disabled = false,
}: GoogleButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = () => {
    setIsLoading(true);
    if (onClick) {
      onClick();
    } else if (href) {
      window.location.href = href;
    }
  };

  // Wrap content in a stable div to prevent DOM conflicts with browser extensions
  // (password managers, Google credential manager) that may inject/modify button children
  return (
    <Button
      variant="outline"
      className="w-full h-11 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-gray-700 font-medium shadow-sm transition-all"
      onClick={handleClick}
      disabled={disabled || isLoading}
    >
      <span className="flex items-center justify-center pointer-events-none">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="18" 
              height="18" 
              viewBox="0 0 48 48"
              className="mr-2"
            >
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C33.553,6.053,28.977,4,24,4C12.955,4,4,12.955,4,24 s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.343,18.961,14,24,14c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C33.553,6.053,28.977,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
              <path fill="#4CAF50" d="M24,44c4.874,0,9.292-1.867,12.625-4.902l-5.828-4.939C29.668,35.524,26.999,36.5,24,36.5 c-5.202,0-9.617-3.317-11.283-7.946l-6.54,5.037C9.488,39.556,16.227,44,24,44z"/>
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.106,5.559 c0.001-0.001,0.002-0.001,0.003-0.002l5.828,4.939C36.668,39.316,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
            </svg>
            {text}
          </>
        )}
      </span>
    </Button>
  );
}
